import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AdminPaymentOpsBookingDto,
  AdminPaymentOpsOverviewDto,
  cancelAdminPaymentAuthorization,
  getAdminPaymentOpsBookings,
  getAdminPaymentOpsOverview,
  PageDto,
  retryAdminPaymentCapture,
  useApiClient,
} from '../api/client';
import { EmptyRow, FilterBar, MetricCard, PageHeader, PageShell, PaginationBar, StatusBadge, toneForPaymentStatus, toneForRideStatus } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';

const issueOptions = [
  'ALL',
  'CAPTURE_FAILED',
  'COMPLETED_NOT_CAPTURED',
  'CAPTURED_WITHOUT_CONNECT_ROUTING',
  'DRIVER_CONNECT_INCOMPLETE',
  'DRIVER_CONNECT_IN_REVIEW',
  'CONNECT_STATUS_STALE',
  'INACTIVE_WITH_OPEN_INTENT',
  'STATE_MISMATCH',
  'PAYMENT_INTENT_MISSING',
];

const statusOptions = ['', 'NOT_REQUIRED', 'AUTHORIZED', 'CAPTURE_PENDING', 'CAPTURED', 'CAPTURE_FAILED', 'CANCELLED'];
const DEFAULT_PAGE_SIZE = 10;

function formatMoney(cents?: number | null) {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
}

function formatName(user?: { email?: string | null; firstName?: string | null; lastName?: string | null } | null) {
  if (!user) return 'Unknown';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || 'Unknown';
}

function formatMaybeDate(value?: string | null) {
  return value ? formatDateTime(value) : '-';
}

function copyText(value?: string | null) {
  if (!value) return;
  void navigator.clipboard?.writeText(value);
}

const issueLabels: Record<string, string> = {
  CAPTURE_FAILED: 'Capture failed',
  COMPLETED_NOT_CAPTURED: 'Completed, not captured',
  CAPTURED_WITHOUT_CONNECT_ROUTING: 'Captured without Connect routing',
  DRIVER_CONNECT_INCOMPLETE: 'Driver Connect incomplete',
  DRIVER_CONNECT_IN_REVIEW: 'Driver Connect in review',
  CONNECT_STATUS_STALE: 'Connect status stale',
  INACTIVE_WITH_OPEN_INTENT: 'Inactive with open intent',
  STATE_MISMATCH: 'State mismatch',
  PAYMENT_INTENT_MISSING: 'Missing intent',
};

function issueClasses(issue: string) {
  switch (issue) {
    case 'CAPTURE_FAILED':
    case 'PAYMENT_INTENT_MISSING':
    case 'STATE_MISMATCH':
    case 'CAPTURED_WITHOUT_CONNECT_ROUTING':
      return 'bg-red-50 text-red-800 ring-red-200';
    case 'COMPLETED_NOT_CAPTURED':
    case 'INACTIVE_WITH_OPEN_INTENT':
    case 'DRIVER_CONNECT_INCOMPLETE':
    case 'DRIVER_CONNECT_IN_REVIEW':
    case 'CONNECT_STATUS_STALE':
      return 'bg-amber-50 text-amber-800 ring-amber-200';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200';
  }
}

function canRetryCapture(booking: AdminPaymentOpsBookingDto) {
  return booking.completedAt != null
    && booking.cancelledAt == null
    && booking.expiredAt == null
    && booking.paymentCaptureStatus !== 'CAPTURED'
    && booking.paymentCaptureStatus !== 'CANCELLED';
}

function canCancelAuthorization(booking: AdminPaymentOpsBookingDto) {
  return !!booking.stripePaymentIntentId
    && booking.paymentCaptureStatus !== 'CAPTURED'
    && booking.paymentCaptureStatus !== 'CANCELLED'
    && (booking.cancelledAt != null || booking.expiredAt != null || booking.rejectedAt != null || booking.rideStatus === 'CANCELLED' || booking.rideStatus === 'EXPIRED');
}

export const PaymentOpsPage: React.FC = () => {
  const { t } = useI18n();
  const [search, setSearch] = useSearchParams();
  const bookingId = search.get('bookingId') || undefined;
  const { request } = useApiClient();
  const { showError, showSuccess } = useToast();
  const [overview, setOverview] = React.useState<AdminPaymentOpsOverviewDto | null>(null);
  const [page, setPage] = React.useState<PageDto<AdminPaymentOpsBookingDto> | null>(null);
  const [issue, setIssue] = React.useState('ALL');
  const [status, setStatus] = React.useState('');
  const [q, setQ] = React.useState('');
  const [pageNumber, setPageNumber] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = React.useState(false);
  const [retrying, setRetrying] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, bookingsData] = await Promise.all([
        getAdminPaymentOpsOverview(request),
        getAdminPaymentOpsBookings(request, { page: pageNumber, size: pageSize, issue, status: status || undefined, q, bookingId }),
      ]);
      setOverview(overviewData);
      setPage(bookingsData);
    } catch (err) {
      showError((err as Error)?.message || 'Failed to load payment ops');
    } finally {
      setLoading(false);
    }
  }, [bookingId, issue, pageNumber, pageSize, q, request, showError, status]);

  React.useEffect(() => {
    const timeout = window.setTimeout(load, 250);
    return () => window.clearTimeout(timeout);
  }, [load]);

  async function retryCapture(bookingId: string) {
    if (!window.confirm(t('paymentOps.confirmRetry'))) return;
    setRetrying(bookingId);
    try {
      await retryAdminPaymentCapture(request, bookingId);
      showSuccess(t('paymentOps.retryFinished'));
      await load();
    } catch (err) {
      showError((err as Error)?.message || t('paymentOps.retryFailed'));
    } finally {
      setRetrying(null);
    }
  }

  async function cancelAuthorization(bookingId: string) {
    if (!window.confirm(t('paymentOps.confirmCancelAuth'))) return;
    setRetrying(bookingId);
    try {
      await cancelAdminPaymentAuthorization(request, bookingId);
      showSuccess(t('paymentOps.authCancelled'));
      await load();
    } catch (err) {
      showError((err as Error)?.message || t('paymentOps.authCancelFailed'));
    } finally {
      setRetrying(null);
    }
  }

  const rows = page?.content || [];
  const issueLabel = React.useCallback((value: string) => t(`paymentOps.issue.${value}`), [t]);

  return (
    <PageShell>
        <PageHeader
          title={t('paymentOps.title')}
          description={t('paymentOps.description')}
          actions={<button className="btn btn-neutral" disabled={loading} onClick={load}>{t('action.refresh')}</button>}
        />

        {overview && (
          <div className="mb-6 grid gap-3 md:grid-cols-6">
            <MetricCard label={t('paymentOps.metric.allBookings')} value={overview.totalBookingCount} tone="slate" />
            <MetricCard label={t('paymentOps.metric.authorized')} value={overview.authorizedCount} tone="blue" />
            <MetricCard label={t('paymentOps.metric.pending')} value={overview.capturePendingCount} tone="blue" />
            <MetricCard label={t('paymentOps.metric.captured')} value={overview.capturedCount} tone="green" />
            <MetricCard label={t('paymentOps.metric.cancelled')} value={overview.cancelledCount} tone="slate" />
            <MetricCard label={t('paymentOps.metric.notRequired')} value={overview.notRequiredCount} tone="slate" />
            <MetricCard label={t('paymentOps.metric.captureFailed')} value={overview.captureFailedCount} tone="red" />
            <MetricCard label={t('paymentOps.metric.notCaptured')} value={overview.completedNotCapturedCount} tone="amber" />
            <MetricCard label={t('paymentOps.metric.noConnectRouting')} value={overview.capturedWithoutConnectRoutingCount} tone="red" />
            <MetricCard label={t('paymentOps.metric.connectIncomplete')} value={overview.driverConnectIncompleteCount} tone="amber" />
            <MetricCard label={t('paymentOps.metric.connectStale')} value={overview.connectStatusStaleCount} tone="violet" />
            <MetricCard label={t('paymentOps.metric.openIntents')} value={overview.inactiveWithOpenIntentCount} tone="slate" />
            <MetricCard label={t('paymentOps.metric.stateMismatch')} value={overview.stateMismatchCount} tone="violet" />
            <MetricCard label={t('paymentOps.metric.missingIntent')} value={overview.paymentIntentMissingCount} tone="red" />
          </div>
        )}

        {bookingId && <div className="mb-4 flex items-center gap-3 rounded-lg border bg-white p-3 text-sm">
          <span>{t('bookings.exactFilter')} <span className="font-mono">{bookingId}</span></span>
          <button className="btn btn-neutral" onClick={() => { const next = new URLSearchParams(search); next.delete('bookingId'); setSearch(next); setPageNumber(0); }}>{t('action.clear')}</button>
        </div>}
        <FilterBar className="lg:grid-cols-[1fr_220px_190px_150px_auto]">
          <input className="form-control" placeholder={t('paymentOps.searchPlaceholder')} value={q} onChange={(e) => { setQ(e.target.value); setPageNumber(0); }} />
          <select className="form-control" value={issue} onChange={(e) => { setIssue(e.target.value); setPageNumber(0); }}>
            {issueOptions.map((value) => <option key={value} value={value}>{issueLabel(value)}</option>)}
          </select>
          <select className="form-control" value={status} onChange={(e) => { setStatus(e.target.value); setPageNumber(0); }}>
            {statusOptions.map((value) => <option key={value || 'all'} value={value}>{value || t('paymentOps.anyStatus')}</option>)}
          </select>
          <select className="form-control" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPageNumber(0); }}>
            {[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size} / page</option>)}
          </select>
          <button className="btn btn-neutral" disabled={loading} onClick={load}>{t('action.apply')}</button>
        </FilterBar>

        <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
          <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
            <span>{loading ? t('paymentOps.loading') : t('paymentOps.bookings', { count: page?.totalElements || 0 })}</span>
            <span>{t('common.pageOf', { page: (page?.number || 0) + 1, total: Math.max(page?.totalPages || 1, 1) })}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 font-semibold">Booking</th>
                  <th className="px-4 py-3 font-semibold">People</th>
                  <th className="px-4 py-3 font-semibold">Ride</th>
                  <th className="px-4 py-3 font-semibold">Payment</th>
                  <th className="px-4 py-3 font-semibold">Connect routing</th>
                  <th className="px-4 py-3 font-semibold">Money</th>
                  <th className="px-4 py-3 font-semibold">Issues</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((booking) => (
                  <tr key={booking.bookingId} className="border-t align-top">
                    <td className="px-4 py-3">
                      <button className="font-mono text-xs text-gray-600 hover:text-gray-900" onClick={() => copyText(booking.bookingId)}>{booking.bookingId}</button>
                      <Link className="mt-1 block text-xs font-semibold text-gray-900 hover:underline" to={`/rides/bookings/${encodeURIComponent(booking.bookingId)}`}>{t('bookings.lifecycle')}</Link>
                      <div className="mt-1 text-xs text-gray-500">Request {booking.rideRequestId || '-'}</div>
                      <div className="text-xs text-gray-500">Created {formatMaybeDate(booking.bookingCreatedAt)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>Driver: <Link className="font-semibold hover:underline" to={`/users/${booking.driver.id}`}>{formatName(booking.driver)}</Link></div>
                      {booking.rider && <div>Rider: <Link className="font-semibold hover:underline" to={`/users/${booking.rider.id}`}>{formatName(booking.rider)}</Link></div>}
                    </td>
                    <td className="px-4 py-3">
                      <Link className="font-semibold hover:underline" to={`/rides/${booking.rideId}`}>{formatMaybeDate(booking.rideTime)}</Link>
                      <div className="text-xs text-gray-500">{booking.departure.publicName} to {booking.destination.publicName}</div>
                      <StatusBadge tone={toneForRideStatus(booking.rideStatus)} className="mt-2">{booking.rideStatus}</StatusBadge>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge tone={toneForPaymentStatus(booking.paymentCaptureStatus)}>{booking.paymentCaptureStatus || 'UNKNOWN'}</StatusBadge>
                      <button className="max-w-[180px] truncate text-xs text-gray-500 hover:text-gray-900" onClick={() => copyText(booking.stripePaymentIntentId)}>{booking.stripePaymentIntentId || 'No intent'}</button>
                      <div className="text-xs text-gray-500">Retries {booking.paymentCaptureRetryCount || 0} - last {formatMaybeDate(booking.paymentCaptureLastAttemptAt)}</div>
                      {booking.paymentCaptureLastError && <div className="mt-1 max-w-xs text-xs text-red-700">{booking.paymentCaptureLastError}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge tone={booking.connectRoutingReady ? 'green' : booking.connectRoutingExpected ? 'amber' : 'slate'}>
                        {booking.connectRoutingExpected ? (booking.connectRoutingReady ? 'Connect ready' : 'Connect blocked') : 'Not routed'}
                      </StatusBadge>
                      <div className="mt-2 text-xs text-gray-500">Mode {booking.compensationMode || '-'}</div>
                      <div className="text-xs text-gray-500">Requires payment {booking.requiresPayment ? 'yes' : 'no'}</div>
                      <button className="max-w-[190px] truncate text-xs text-gray-500 hover:text-gray-900" onClick={() => copyText(booking.expectedDestinationAccountId)}>
                        {booking.expectedDestinationAccountId || 'No destination account'}
                      </button>
                      {booking.connectStatusStale && <div className="mt-1 text-xs font-semibold text-amber-700">Connect status stale</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{formatMoney(booking.priceInCents)}</div>
                      <div className="text-xs text-gray-500">Fee {formatMoney(booking.serviceFeeInCents)}</div>
                      <div className="text-xs text-gray-500">Driver {formatMoney(booking.driverEarningsInCents)}</div>
                      <div className="mt-1 text-xs text-gray-500">Estimate {formatMoney(booking.platformPriceEstimateInCents)} / fee {formatMoney(booking.platformServiceFeeEstimateInCents)}</div>
                      <div className="text-xs text-gray-500">Expected destination {formatMoney(booking.expectedDestinationAmountInCents)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex max-w-xs flex-wrap gap-1">
                        {booking.issues.map((item) => <span key={item} className={`rounded-full px-2 py-1 text-xs font-semibold ring-1 ${issueClasses(item)}`}>{issueLabel(item) || issueLabels[item] || item}</span>)}
                        {booking.issues.length === 0 && <span className="text-xs text-gray-500">No issue</span>}
                      </div>
                      <div className="mt-2 grid gap-0.5 text-[11px] text-gray-500">
                        <div>Approved {formatMaybeDate(booking.approvedAt)}</div>
                        <div>Rejected {formatMaybeDate(booking.rejectedAt)}</div>
                        <div>Picked up {formatMaybeDate(booking.pickedUpAt)}</div>
                        <div>Completed {formatMaybeDate(booking.completedAt)}</div>
                        <div>Cancelled {formatMaybeDate(booking.cancelledAt)}</div>
                        <div>Expired {formatMaybeDate(booking.expiredAt)}</div>
                      </div>
                    </td>
                    <td className="space-y-2 px-4 py-3">
                      <button
                        className="btn btn-primary w-full justify-center"
                        disabled={retrying === booking.bookingId || !canRetryCapture(booking)}
                        onClick={() => retryCapture(booking.bookingId)}
                      >
                        {retrying === booking.bookingId ? t('paymentOps.retrying') : t('paymentOps.retryCapture')}
                      </button>
                      <button
                        className="btn btn-danger w-full justify-center"
                        disabled={retrying === booking.bookingId || !canCancelAuthorization(booking)}
                        onClick={() => cancelAuthorization(booking.bookingId)}
                      >
                        {t('paymentOps.cancelAuth')}
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && <EmptyRow colSpan={8}>{t('paymentOps.noMatches')}</EmptyRow>}
              </tbody>
            </table>
          </div>
          <PaginationBar
            loading={loading}
            pageNumber={pageNumber}
            pageSize={pageSize}
            rowCount={rows.length}
            totalElements={page?.totalElements}
            isLast={page?.last}
            onPrevious={() => setPageNumber((p) => Math.max(0, p - 1))}
            onNext={() => setPageNumber((p) => p + 1)}
            previousLabel={t('action.previous')}
            nextLabel={t('action.next')}
          />
        </div>
    </PageShell>
  );
};

export default PaymentOpsPage;
